import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, FileText, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';
import FileUpload from '../../components/FileUpload';

const DocumentCreate = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useAlert();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [creationMethod, setCreationMethod] = useState('editor'); // 'editor' or 'upload'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    document_type: 'SOP',
    category: 'Safety',
    department: 'Operations',
    location: '',
    applicable_roles: [],
    keywords: '',
    mandatory_reading: false,
    target_readers: [],
    content: '' // For editor mode
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFilesChange = (files) => {
    setUploadedFiles(files);
  };

  const handleFileUploadError = (errors) => {
    errors.forEach(err => showError(err));
  };

  const handleMultiSelect = (e, field) => {
    const options = Array.from(e.target.selectedOptions, option => option.value);
    setFormData({ ...formData, [field]: options });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Step 1: Create document metadata
      const keywordsArray = formData.keywords.split(',').map(k => k.trim()).filter(k => k);
      const docPayload = {
        ...formData,
        keywords: keywordsArray
      };

      const createResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/create`,
        docPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const docId = createResponse.data.id;

      // Step 2: Upload file or content
      if (creationMethod === 'upload' && uploadedFiles.length > 0) {
        // Get the file to upload (from local files, not already uploaded)
        const fileToUpload = uploadedFiles[0];
        if (fileToUpload.file && !fileToUpload.uploaded) {
          const formDataUpload = new FormData();
          formDataUpload.append('file', fileToUpload.file);
          formDataUpload.append('version_type', 'Minor');
          formDataUpload.append('change_description', 'Initial version');

          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/upload`,
            formDataUpload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
              }
            }
          );
        }
      } else if (creationMethod === 'editor' && formData.content) {
        // Update content via editor
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/content`,
          {
            content: formData.content,
            version_type: 'Minor',
            change_description: 'Initial version'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      showSuccess('Document created successfully!');
      navigate('/documents');
    } catch (error) {
      console.error('Error creating document:', error);
      showError(error.response?.data?.detail || 'Failed to create document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/documents')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Documents
          </Button>

          <h1 className="text-3xl font-bold text-gray-900">Create New Document</h1>
          <p className="text-gray-600 mt-1">Create an SOP, policy, or procedure</p>
        </div>

        {/* Creation Method Selection */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">How would you like to create this document?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setCreationMethod('editor')}
              className={`p-6 border-2 rounded-lg text-left transition-all ${
                creationMethod === 'editor'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <FileText className="w-8 h-8 text-blue-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Browser Editor</h3>
              <p className="text-sm text-gray-600 mt-1">Create content directly in the browser</p>
            </button>

            <button
              onClick={() => setCreationMethod('upload')}
              className={`p-6 border-2 rounded-lg text-left transition-all ${
                creationMethod === 'upload'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <Upload className="w-8 h-8 text-blue-600 mb-2" />
              <h3 className="font-semibold text-gray-900">Upload File</h3>
              <p className="text-sm text-gray-600 mt-1">Upload an existing document file</p>
            </button>
          </div>
        </div>

        {/* Document Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Document Information</h2>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Lockout/Tagout Procedure"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type *
              </label>
              <select
                name="document_type"
                value={formData.document_type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="SOP">SOP</option>
                <option value="Work_Instruction">Work Instruction</option>
                <option value="Policy">Policy</option>
                <option value="Form">Form</option>
                <option value="Checklist">Checklist</option>
                <option value="Manual">Manual</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the document"
            />
          </div>

          {/* Classification */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Safety">Safety</option>
                <option value="Operations">Operations</option>
                <option value="Quality">Quality</option>
                <option value="Environmental">Environmental</option>
                <option value="HR">HR</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department *
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Operations">Operations</option>
                <option value="Maintenance">Maintenance</option>
                <option value="HSE">HSE</option>
                <option value="Quality">Quality</option>
                <option value="Engineering">Engineering</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Plant A, Building 2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keywords (comma-separated)
            </label>
            <input
              type="text"
              name="keywords"
              value={formData.keywords}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., lockout, tagout, energy isolation"
            />
          </div>

          {/* Applicable Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Applicable Roles (Hold Ctrl/Cmd to select multiple)
            </label>
            <select
              multiple
              value={formData.applicable_roles}
              onChange={(e) => handleMultiSelect(e, 'applicable_roles')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-24"
            >
              <option value="Safety Manager">Safety Manager</option>
              <option value="Safety Officer">Safety Officer</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Operator">Operator</option>
              <option value="Technician">Technician</option>
              <option value="Engineer">Engineer</option>
              <option value="Contractor">Contractor</option>
            </select>
            {formData.applicable_roles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.applicable_roles.map(role => (
                  <span key={role} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                    {role}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Mandatory Reading */}
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="mandatory_reading"
                checked={formData.mandatory_reading}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Mandatory Reading (requires acknowledgment)
              </span>
            </label>
          </div>

          {/* Target Readers */}
          {formData.mandatory_reading && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Readers (Hold Ctrl/Cmd to select multiple) *
              </label>
              <select
                multiple
                value={formData.target_readers}
                onChange={(e) => handleMultiSelect(e, 'target_readers')}
                required={formData.mandatory_reading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-32"
              >
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.role})
                  </option>
                ))}
              </select>
              {formData.target_readers.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">{formData.target_readers.length} user(s) selected</p>
                </div>
              )}
            </div>
          )}

          {/* Content / File Upload */}
          {creationMethod === 'editor' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Content
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows={15}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="Write your document content here...\n\nYou can use this editor to create procedures, instructions, and policies."
              />
              <p className="text-sm text-gray-500 mt-1">
                Tip: You can paste content from Word, Google Docs, or other sources.
              </p>
            </div>
          )}

          {creationMethod === 'upload' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Document File
              </label>
              <FileUpload
                files={uploadedFiles}
                onFilesChange={handleFilesChange}
                multiple={false}
                accept=".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png"
                maxSizeMB={50}
                allowedTypes={['.pdf', '.docx', '.xlsx', '.pptx', '.jpg', '.jpeg', '.png']}
                label="Click to upload or drag and drop"
                showSizeLimit={true}
                onError={handleFileUploadError}
              />
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/documents')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || (creationMethod === 'upload' && uploadedFiles.length === 0) || (creationMethod === 'editor' && !formData.content)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Create Document
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentCreate;
